import { useCallback, useEffect, useState } from "react";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ReceiptText, FileDown } from "lucide-react";

const STATUS_STYLE = {
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  APPROVED_SAMPLE: "bg-green-100 text-green-800 border-green-300",
  MATERIALITY_BOUND: "bg-green-100 text-green-800 border-green-300",
  AUDITOR_APPROVED_THRESHOLD: "bg-green-100 text-green-800 border-green-300",
  MATCHED_BANK_STRONG_REFERENCE: "bg-green-100 text-green-800 border-green-300",
  PROPOSED: "bg-amber-100 text-amber-800 border-amber-300",
  DRAFT: "bg-slate-100 text-slate-700 border-slate-300",
  SYSTEM_GENERATED_PROPOSED_SAMPLE: "bg-amber-100 text-amber-800 border-amber-300",
  MATERIALITY_NOT_SET: "bg-amber-100 text-amber-800 border-amber-300",
  SYSTEM_PROPOSED_THRESHOLD: "bg-amber-100 text-amber-800 border-amber-300",
  STALE: "bg-red-100 text-red-800 border-red-300",
  SUPERSEDED: "bg-slate-100 text-slate-700 border-slate-300",
  BANK_SOURCE_NOT_REGISTERED: "bg-slate-100 text-slate-700 border-slate-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  COMPLETE: "bg-green-100 text-green-800 border-green-300",
  READY: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_SUBSTANTIVE_TEST: "bg-green-100 text-green-800 border-green-300",
  MATCHED_EXPENSE_PURCHASE: "bg-green-100 text-green-800 border-green-300",
  NORMAL_POPULATION: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  MEDIUM_RISK_INDICATOR: "bg-amber-100 text-amber-800 border-amber-300",
  INCOMPLETE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_PERIOD: "bg-red-100 text-red-800 border-red-300",
  AMBIGUOUS: "bg-red-100 text-red-800 border-red-300",
  HIGH_RISK_INDICATOR: "bg-red-100 text-red-800 border-red-300",
  NOT_READY_FOR_SUBSTANTIVE_TEST: "bg-red-100 text-red-800 border-red-300",
  SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
  BANK_SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
};

const NOT_AVAILABLE = "Tidak berlaku / sumber tidak tersedia";
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_SOURCE: "Tertahan — sumber belum tersedia",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun belum disetujui",
  BLOCKED_PERIOD: "Tertahan — periode belum lengkap",
  REVIEW_REQUIRED: "Perlu reviu",
};
const LABELS = {
  DRAFT: "Draf", PROPOSED: "Diajukan", APPROVED: "Disetujui", STALE: "Usang",
  SUPERSEDED: "Digantikan",
  SYSTEM_GENERATED_PROPOSED_SAMPLE: "Sampel Usulan",
  APPROVED_SAMPLE: "Sampel Disetujui",
  MATERIALITY_BOUND: "Terikat pada materialitas perikatan",
  MATERIALITY_NOT_SET: "Materialitas belum ditetapkan",
  SYSTEM_PROPOSED_THRESHOLD: "Ambang usulan sistem",
  AUDITOR_APPROVED_THRESHOLD: "Ambang disetujui auditor",
  AUDITOR_SPECIFIED_SAMPLE_SIZE: "Ukuran ditetapkan auditor",
  SYSTEM_SUGGESTED_SAMPLE_SIZE: "Ukuran usulan sistem",
  MATCHED_BANK_STRONG_REFERENCE: "Cocok — referensi pada uraian bank",
  MATCHED_BANK_MULTI_ATTRIBUTE: "Cocok — beberapa atribut",
  MATCHED_THROUGH_AP: "Melalui hutang usaha",
  PARTIAL_PAYMENT: "Pembayaran sebagian",
  BANK_TRANSACTION_NOT_FOUND: "Tidak ditemukan di bank",
  BANK_SOURCE_NOT_REGISTERED: "Rekening bank belum terdaftar pada perikatan",
  POPULATION_CHANGED: "Populasi berubah", MATERIALITY_CHANGED: "Materialitas berubah",
  THRESHOLD_CHANGED: "Ambang berubah",
  AMBIGUOUS: "Belum disetujui", COMPLETE: "Lengkap",
  INCOMPLETE: "Belum lengkap", REVIEW_REQUIRED: "Perlu reviu",
  SOURCE_NOT_SUPPLIED: NOT_AVAILABLE, BANK_SOURCE_NOT_SUPPLIED: "Rekening koran belum terdaftar",
  MATCHED_EXPENSE_PURCHASE: "Tertelusur ke pembelian",
  EXPENSE_WITHOUT_PURCHASE_SOURCE: "Tanpa dokumen pembelian",
  PURCHASE_NOT_EXPECTED: "Dokumen pembelian memang tidak diharapkan",
  CAPITAL_OR_INVENTORY_CLASSIFICATION: "Dokumen membebani akun neraca",
  AMOUNT_DIFFERENCE: "Perbedaan nilai", TIMING_DIFFERENCE: "Perbedaan waktu",
  MATCHED_EXPENSE_VAT: "Didukung PPN Masukan", EXPENSE_WITHOUT_VAT: "Tanpa PPN Masukan",
  VAT_NOT_APPLICABLE: "PPN tidak berlaku untuk jenis beban ini",
  VAT_TIMING_DIFFERENCE: "PPN pada jurnal, dokumen tidak di daftar",
  MATCHED_EXPENSE_WHT: "Didukung pemotongan", WHT_NOT_APPLICABLE: "Pemotongan tidak diharapkan",
  EXPENSE_WITHOUT_EXPECTED_WHT: "Diharapkan dipotong tetapi tidak ada",
  DPP_DIFFERENCE: "Perbedaan DPP", PERIOD_DIFFERENCE: "Perbedaan masa",
  MATCHED_PAYMENT: "Tertelusur ke bank", PAID_DIRECTLY_FROM_BANK: "Dibayar langsung dari kas/bank",
  SETTLED_THROUGH_PAYABLE: "Melalui hutang usaha", UNPAID_AT_PERIOD_END: "Belum terlunasi",
  HIGH_RISK_INDICATOR: "Indikator risiko tinggi", MEDIUM_RISK_INDICATOR: "Indikator risiko sedang",
  NORMAL_POPULATION: "Populasi biasa",
  READY: "Siap", READY_FOR_SUBSTANTIVE_TEST: "Siap untuk pengujian substantif",
  NOT_READY_FOR_SUBSTANTIVE_TEST: "Belum siap untuk pengujian substantif",
  EXPENSE_CLASSIFICATION_SUPPORTED: "Klasifikasi beban didukung",
  CAPITALISATION_CANDIDATE: "Kandidat kapitalisasi", PREPAID_CANDIDATE: "Kandidat biaya dibayar dimuka",
  INVENTORY_CANDIDATE: "Kandidat persediaan", INSUFFICIENT_EVIDENCE: "Bukti tidak memadai",
  PRE_PERIOD: "Sebelum periode", IN_PERIOD: "Dalam periode", POST_PERIOD: "Setelah periode",
  ACCRUAL_CANDIDATE: "Kandidat akrual", LATE_POSTING: "Pembukuan terlambat",
};
const GATE_LABELS = {
  account_mapping_approved: "Pemetaan akun beban disetujui",
  population_scope_defensible: "Lingkup populasi dapat dipertanggungjawabkan",
  exclusions_documented: "Pengecualian terdokumentasi",
  population_controlled: "Jumlah dan nilai populasi terkendali",
  jet_universe_available: "Populasi JET tersedia",
  sampling_universe_available: "Kerangka sampling tersedia",
  sampling_plan_documented: "Rencana sampling terdokumentasi",
  materiality_bound_or_explicit_basis: "Materialitas terikat atau dasar eksplisit",
  sample_plan_approved_by_auditor: "Rencana sampling disetujui auditor",
  selection_reproducible: "Pemilihan dapat diulang",
  official_sample_frozen: "Sampel resmi dibekukan",
  evidence_fields_ready: "Kolom bukti siap",
  auditor_conclusion_blank: "Simpulan auditor kosong",
};
const UNIVERSE_LABELS = {
  HIGH_VALUE: "Nilai tinggi", RANDOM_REPRESENTATIVE: "Acak representatif",
  JET_RISK: "Risiko JET", WHT_EXCEPTION: "Pengecualian PPh", VAT_EXCEPTION: "Pengecualian PPN",
  PERIOD_END_CUTOFF: "Cut-off akhir periode", MANUAL_JOURNAL: "Jurnal manual",
  RELATED_PARTY: "Pihak berelasi", NEGATIVE_OR_CREDIT: "Negatif / kredit",
  NON_PURCHASE_EXPENSE: "Beban tanpa pembelian",
};

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["populasi", "Populasi Beban", (v) => v.population?.controls?.transactions > 0],
  ["pemetaan", "Pemetaan Akun", () => true],
  ["pengecualian", "Pengecualian Populasi", (v) => !!v.exclusions],
  ["bulanan", "Ringkasan Bulanan", (v) => !!v.population?.controls?.by_month],
  ["pembelian", "Beban vs Pembelian", (v) => !!v.expense_vs_purchase],
  ["ppn", "Beban vs PPN", (v) => !!v.expense_vs_vat],
  ["pph", "Beban vs PPh", (v) => !!v.expense_vs_wht],
  ["pembayaran", "Pembayaran / Bank", (v) => !!v.expense_vs_payment],
  ["bank", "Penelusuran Bank", (v) => !!v.expense_vs_bank],
  ["jet", "JET Beban", (v) => !!v.jet],
  ["risiko", "Risk Stratification", (v) => !!v.risk_stratification],
  ["kerangka", "Sampling Universe", (v) => !!v.sampling_universes],
  ["rencana", "Rencana Sampling", (v) => !!v.sampling_plan],
  ["sampel", "Sampel Terpilih", (v) => (v.samples || []).length > 0],
  ["substantif", "Pengujian Substantif", (v) => (v.samples || []).length > 0],
  ["cutoff", "Cut-off", (v) => !!v.cutoff],
  ["capital", "Capital vs Expense", (v) => !!v.capital_vs_expense],
  ["pos", "Pos Rekonsiliasi", (v) => !!v.expense_vs_purchase || !!v.expense_vs_wht],
  ["bukti", "Bukti Audit", () => true],
  ["kertas", "Kertas Kerja", () => true],
];

// Beban — populasi beban logis, penautan lintas siklus, JET dan sampling untuk pengujian substantif.
export default function Expenses({ id }) {
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ringkasan");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/expenses`).then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/expenses-summary`).then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/expenses/workpaper.xlsx`, `kertas-kerja-beban-${id}.xlsx`)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) return <div className="text-xs text-muted-foreground p-4">Memuat data beban…</div>;
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="expenses">
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <ReceiptText size={15} />Beban
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportWorkpaper}
          data-testid="expenses-export">
          <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
        </Button>
      </div>

      {summary && (
        <div className="bg-white border border-border rounded-sm p-2"
          data-testid="expenses-domain-summary">
          <div className="text-xs font-head font-semibold px-1 pb-2">
            Ringkasan Domain Beban
            <Badge status={summary.acceptance}
              label={ACCEPTANCE_LABELS[summary.acceptance] || summary.acceptance} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Figure label="Akun beban dipetakan" value={summary.accounts_mapped} />
            <Figure label="Kategori" value={summary.categories} />
            <Figure label="Transaksi" value={summary.transactions} />
            <Figure label="Jurnal" value={summary.journals} />
            <Figure label="Nilai populasi" value={rupiah(summary.net_expense)} />
            <Figure label="Transaksi dikecualikan" value={summary.excluded_transactions} />
            <Figure label="Cakupan bulan" value={summary.coverage}
              status={summary.completeness_status} />
            <Figure label="Pemetaan akun" value={text(summary.account_mapping_status)}
              status={summary.account_mapping_status} />
            <Figure label="Beban ↔ pembelian" value={text(summary.expense_vs_purchase_status)}
              status={summary.expense_vs_purchase_status} />
            <Figure label="Beban ↔ PPN" value={text(summary.expense_vs_vat_status)}
              status={summary.expense_vs_vat_status} />
            <Figure label="Beban ↔ PPh" value={text(summary.expense_vs_wht_status)}
              status={summary.expense_vs_wht_status} />
            <Figure label="Beban ↔ pembayaran" value={text(summary.expense_vs_payment_status)}
              status={summary.expense_vs_payment_status} />
            <Figure label="Aturan JET dijalankan" value={summary.jet_rules_executed} />
            <Figure label="Transaksi bertanda JET" value={summary.jet_transactions_with_a_hit} />
            <Figure label="Indikator risiko tinggi" value={summary.high_risk_indicator} />
            <Figure label="Indikator anomali" value={summary.anomaly_findings} />
            <Figure label="Pos cut-off" value={summary.cutoff_items} />
            <Figure label="Kandidat kapitalisasi" value={summary.capitalisation_candidates} />
            <Figure label="Kerangka sampling terisi" value={summary.sampling_universes} />
            <Figure label="Sampel dipilih" value={summary.samples_selected} />
            <Figure label="Transaksi terpilih" value={summary.sample_transactions} />
            <Figure label="Cakupan nilai sampel" value={summary.value_coverage} />
            <Figure label="Status rencana sampling" value={text(summary.sample_plan_status)}
              status={summary.sample_plan_status} />
            <Figure label="Status sampel" value={text(summary.sample_status)}
              status={summary.sample_status} />
            <Figure label="Disetujui oleh" value={summary.sample_plan_approved_by || "—"} />
            <Figure label="Penelusuran bank" value={text(summary.expense_vs_bank_status)}
              status={summary.expense_vs_bank_status} />
            <Figure label="Rekening bank terdaftar" value={summary.bank_accounts_registered} />
            <Figure label="Kesiapan pengujian" value={text(summary.sampling_readiness)}
              status={summary.sampling_readiness} />
            <Figure label="Materialitas" value={summary.materiality ? rupiah(summary.materiality)
              : "Belum ditetapkan"} />
          </div>
          {!summary.materiality && (
            <div className="text-xs text-amber-800 px-1 pt-2"
              data-testid="expenses-materiality-note">
              Perikatan belum memuat materialitas. Ukuran sampel yang bergantung pada materialitas
              tidak dihitung dan materialitas tidak diasumsikan; ukuran sampel harus ditetapkan
              auditor secara eksplisit.
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`expenses-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto p-2" data-testid={`expenses-panel-${tab}`}>
          {tab === "ringkasan" && <Sources view={view} />}
          {tab === "populasi" && <Population population={view.population} />}
          {tab === "pemetaan" && <Mapping mapping={view.account_mapping} />}
          {tab === "pengecualian" && <Exclusions exclusions={view.exclusions} />}
          {tab === "bulanan" && <Monthly controls={view.population?.controls} />}
          {tab === "pembelian" && <Link result={view.expense_vs_purchase} origin="pembelian" />}
          {tab === "ppn" && <Link result={view.expense_vs_vat} origin="ppn" />}
          {tab === "pph" && <Link result={view.expense_vs_wht} origin="pph" />}
          {tab === "pembayaran" && <Link result={view.expense_vs_payment} origin="pembayaran" />}
          {tab === "jet" && <Jet jet={view.jet} hits={view.jet_hits} />}
          {tab === "risiko" && <Risk risk={view.risk_stratification} />}
          {tab === "kerangka" && <Universes result={view.sampling_universes} />}
          {tab === "rencana" && <Plan plan={view.sampling_plan} onChanged={load} id={id} />}
          {tab === "bank" && <BankTrace result={view.expense_vs_bank} />}
          {tab === "sampel" && <Samples samples={view.samples} coverage={view.coverage}
            plan={view.sampling_plan} />}
          {tab === "substantif" && <Substantive samples={view.samples} />}
          {tab === "cutoff" && <Cutoff result={view.cutoff} />}
          {tab === "capital" && <Capital result={view.capital_vs_expense} />}
          {tab === "pos" && <Open view={view} />}
          {tab === "bukti" && <Evidence rows={view.evidence} population={view.gl_population} />}
          {tab === "kertas" && <Readiness readiness={view.sampling_readiness} />}
        </div>
      </div>
    </div>
  );
}

function text(status) {
  return LABELS[status] || status || "—";
}

function Badge({ status, label }) {
  return (
    <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}`}>
      {label || text(status)}
    </span>
  );
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Empty({ id }) {
  return <div className="text-xs text-muted-foreground p-2" data-testid={id}>{NOT_AVAILABLE}</div>;
}

function Sources({ view }) {
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-sources">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Sumber</th><th>Populasi logis</th><th className="text-right">Baris</th></tr></thead>
        <tbody>{(view.evidence || []).map((row, index) => (
          <tr key={index}>
            <td>{row.source}</td><td className="font-mono">{row.logical_id}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
          </tr>))}
        </tbody>
      </table>
      {view.gl_population?.note && (
        <div className="text-amber-800" data-testid="expenses-gl-note">{view.gl_population.note}</div>
      )}
      <div className="text-muted-foreground">{view.population?.basis}</div>
    </div>
  );
}

function Population({ population }) {
  const controls = population?.controls || {};
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-population">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kategori</th><th className="text-right">Transaksi</th>
          <th className="text-right">Beban neto</th></tr></thead>
        <tbody>{Object.entries(controls.by_category || {}).map(([name, entry]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{entry.transactions}</td>
            <td className="text-right tabular-nums">{rupiah(entry.net_expense)}</td></tr>))}
        </tbody>
      </table>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Figure label="Jurnal" value={controls.journals} />
        <Figure label="Akun" value={controls.accounts} />
        <Figure label="Pemasok teridentifikasi" value={controls.vendors} />
        <Figure label="Jurnal manual" value={controls.manual_journals} />
        <Figure label="Pembalik" value={controls.reversals} />
        <Figure label="Baris PPN pada jurnal" value={controls.with_vat_line} />
        <Figure label="Baris PPh pada jurnal" value={controls.with_wht_line} />
        <Figure label="Baris kas/bank pada jurnal" value={controls.with_bank_line} />
      </div>
      <div className="text-muted-foreground" data-testid="expenses-population-basis">
        {population?.basis}
      </div>
    </div>
  );
}

function Mapping({ mapping }) {
  const grouped = {};
  Object.entries(mapping?.expense_accounts || {}).forEach(([code, category]) => {
    (grouped[category] = grouped[category] || []).push(code);
  });
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-mapping">
      <div>Status<Badge status={mapping?.status} /></div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kategori beban</th><th className="text-right">Akun</th><th>Kode akun</th></tr></thead>
        <tbody>{Object.entries(grouped).map(([category, codes]) => (
          <tr key={category}><td>{category}</td>
            <td className="text-right tabular-nums">{codes.length}</td>
            <td className="max-w-md">{codes.join(", ")}</td></tr>))}
        </tbody>
      </table>
      <div>
        Kategori yang diharapkan dipotong pajak:{" "}
        {(mapping?.wht_expected_categories || []).join(", ") || "—"}
      </div>
      <div>
        Ambang nilai tinggi:{" "}
        {mapping?.high_value_threshold ? rupiah(mapping.high_value_threshold)
          : "belum ditetapkan auditor"}
      </div>
    </div>
  );
}

function Exclusions({ exclusions }) {
  if (!exclusions) return <Empty id="expenses-exclusions-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-exclusions">
      <div>{exclusions.transactions} transaksi dikeluarkan dari populasi beban, masing-masing
        dengan klasifikasi dan alasannya. Tidak ada transaksi yang dihapus diam-diam.</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Klasifikasi</th><th className="text-right">Transaksi</th>
          <th className="text-right">Nilai</th><th>Alasan</th></tr></thead>
        <tbody>{Object.entries(exclusions.summary || {}).map(([name, entry]) => (
          <tr key={name}><td>{name}</td>
            <td className="text-right tabular-nums">{entry.transactions}</td>
            <td className="text-right tabular-nums">{rupiah(entry.net_expense)}</td>
            <td className="max-w-lg">{entry.reason}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Monthly({ controls }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="expenses-monthly">
      <thead><tr><th>Masa</th><th className="text-right">Transaksi</th>
        <th className="text-right">Beban neto</th></tr></thead>
      <tbody>{Object.entries(controls?.by_month || {}).sort().map(([month, entry]) => (
        <tr key={month}><td>{month}</td>
          <td className="text-right tabular-nums">{entry.transactions}</td>
          <td className="text-right tabular-nums">{rupiah(entry.net_expense)}</td></tr>))}
      </tbody>
    </table>
  );
}

function Link({ result, origin }) {
  if (!result) return <Empty id={`expenses-${origin}-empty`} />;
  const summary = result.summary || {};
  return (
    <div className="space-y-2 text-xs" data-testid={`expenses-${origin}`}>
      <div>
        {summary.coverage && <>Cakupan {summary.coverage}</>}
        <Badge status={summary.status} />
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Status</th><th className="text-right">Transaksi</th></tr></thead>
        <tbody>{Object.entries(summary.by_status || {}).map(([status, count]) => (
          <tr key={status}><td>{text(status)}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground" data-testid={`expenses-${origin}-basis`}>
        {result.basis}
      </div>
    </div>
  );
}

function Jet({ jet, hits }) {
  if (!jet) return <Empty id="expenses-jet-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-jet">
      <div>
        {jet.rules_executed} aturan dijalankan atas {jet.transactions_tested} transaksi;{" "}
        {jet.transactions_with_a_hit} transaksi tersentuh setidaknya satu aturan.
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Aturan JET</th><th className="text-right">Kecocokan</th></tr></thead>
        <tbody>{Object.entries(jet.by_rule || {}).map(([rule, count]) => (
          <tr key={rule}><td>{rule}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <table className="w-full audit-table text-xs" data-testid="expenses-jet-hits">
        <thead><tr><th>Transaksi</th><th>Tanggal</th><th className="text-right">Nilai</th>
          <th>Aturan</th><th>Alasan risiko</th><th>Simpulan auditor</th></tr></thead>
        <tbody>{(hits || []).slice(0, 200).map((hit, index) => (
          <tr key={index}>
            <td>{hit.source_number}</td><td>{hit.posting_date}</td>
            <td className="text-right tabular-nums">{rupiah(hit.net_expense)}</td>
            <td>{hit.jet_rule}</td><td className="max-w-md">{hit.risk_reason}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-amber-800" data-testid="expenses-jet-note">{jet.note}</div>
    </div>
  );
}

function Risk({ risk }) {
  if (!risk) return <Empty id="expenses-risk-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-risk">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Indikator</th><th className="text-right">Transaksi</th></tr></thead>
        <tbody>{Object.entries(risk.by_indicator || {}).map(([name, count]) => (
          <tr key={name}><td>{text(name)}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <div className="text-amber-800" data-testid="expenses-risk-note">{risk.note}</div>
    </div>
  );
}

function Universes({ result }) {
  if (!result) return <Empty id="expenses-universes-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-universes">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kerangka</th><th className="text-right">Jumlah</th>
          <th className="text-right">Nilai</th><th className="text-right">Cakupan nilai</th>
          <th>Satuan sampling</th><th>Pengecualian</th></tr></thead>
        <tbody>{Object.entries(result.universes || {}).map(([name, entry]) => (
          <tr key={name}>
            <td>{UNIVERSE_LABELS[name] || name}</td>
            <td className="text-right tabular-nums">{entry.count}</td>
            <td className="text-right tabular-nums">{rupiah(entry.value)}</td>
            <td className="text-right tabular-nums">{entry.coverage_of_population_value}</td>
            <td>{entry.sampling_unit}</td>
            <td className="max-w-md">{entry.exclusions}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Plan({ plan, onChanged, id }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!plan) return <Empty id="expenses-plan-empty" />;

  const send = (path, body, message) => {
    setBusy(true);
    return api.post(`/engagements/${id}/expense-sample-plan${path}`, body)
      .then(() => { toast.success(message); onChanged(); })
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-3 text-xs" data-testid="expenses-plan">
      <div className="font-medium">
        Status rencana<Badge status={plan.status} />
        <span className="ml-2">Status sampel</span><Badge status={plan.sample_status} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Figure label="Transaksi populasi" value={plan.population_count} />
        <Figure label="Nilai populasi" value={rupiah(plan.population_value)} />
        <Figure label="Cuplikan populasi"
          value={(plan.population_snapshot_id || "").slice(0, 12) + "…"} />
        <Figure label="Materialitas" value={text(plan.materiality_status)}
          status={plan.materiality_status} />
        <Figure label="Materialitas keseluruhan"
          value={plan.overall_materiality ? rupiah(plan.overall_materiality) : "—"} />
        <Figure label="Materialitas pelaksanaan"
          value={plan.performance_materiality ? rupiah(plan.performance_materiality) : "—"} />
        <Figure label="Ambang tidak signifikan"
          value={plan.trivial_threshold ? rupiah(plan.trivial_threshold) : "—"} />
        <Figure label="Versi materialitas" value={plan.materiality_version ?? "—"} />
        <Figure label="Ambang nilai tinggi"
          value={plan.high_value_threshold ? rupiah(plan.high_value_threshold) : "—"} />
        <Figure label="Asal ambang" value={text(plan.high_value_threshold_provenance)}
          status={plan.high_value_threshold_provenance} />
        <Figure label="Diharapkan terpilih" value={plan.expected_distinct_sample_count} />
        <Figure label="Cakupan diharapkan" value={plan.expected_coverage} />
      </div>

      <div className="text-muted-foreground" data-testid="expenses-plan-materiality-basis">
        {plan.materiality_basis}
      </div>
      {plan.high_value_threshold_note && (
        <div className="text-amber-800" data-testid="expenses-plan-threshold-note">
          {plan.high_value_threshold_note}
        </div>
      )}

      <table className="w-full audit-table text-xs" data-testid="expenses-plan-universes">
        <thead><tr><th>Kerangka</th><th>Metode</th><th className="text-right">Ukuran</th>
          <th>Seed</th><th>Asal ukuran</th><th className="text-right">Terpilih</th>
          <th className="text-right">Nilai</th><th className="text-right">Tumpang tindih</th>
          <th>Dasar pemilihan</th></tr></thead>
        <tbody>{Object.entries(plan.per_universe || {}).map(([name, entry]) => (
          <tr key={name}>
            <td>{UNIVERSE_LABELS[name] || name}</td><td>{entry.method}</td>
            <td className="text-right tabular-nums">{entry.sample_size ?? "—"}</td>
            <td className="font-mono">{entry.seed ?? "—"}</td>
            <td>{text(entry.size_provenance)}</td>
            <td className="text-right tabular-nums">{entry.selected_count}</td>
            <td className="text-right tabular-nums">{rupiah(entry.selected_value)}</td>
            <td className="text-right tabular-nums">{entry.overlapping_items}</td>
            <td className="max-w-md">{entry.selection_basis}</td>
          </tr>))}
        </tbody>
      </table>

      {!!(plan.targeted_100_percent_groups || []).length && (
        <div>Diuji 100%: {plan.targeted_100_percent_groups.join(", ")}</div>
      )}

      {plan.status === "APPROVED" && (
        <div className="border border-border rounded-sm p-2" data-testid="expenses-plan-approval">
          <div className="font-medium">Persetujuan</div>
          <div>Disiapkan oleh {plan.prepared_by || "—"} · Disetujui oleh {plan.approved_by} pada{" "}
            {plan.approved_at}</div>
          <div className="text-muted-foreground mt-1">{plan.approval_note}</div>
          <div className="font-mono mt-1">
            Sidik jari pembekuan {(plan.frozen?.freeze_hash || "").slice(0, 24)}…
          </div>
          {plan.reproduction && (
            <div>Pembuatan ulang: {plan.reproduction.status}</div>
          )}
        </div>
      )}

      {!!(plan.stale_reason || []).length && (
        <div className="text-red-800" data-testid="expenses-plan-stale">
          Rencana ini menjadi usang:{" "}
          {plan.stale_reason.map((r) => text(r.reason)).join(", ")}.{" "}
          {plan.stale_reason.map((r) => r.note).join(" ")}
        </div>
      )}

      {!!(plan.blockers || []).length && (
        <div className="text-amber-800" data-testid="expenses-plan-blockers">
          <div className="font-medium">Belum dapat disetujui</div>
          <ul className="list-disc pl-4">
            {plan.blockers.map((b, index) => <li key={index}>{b}</li>)}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <textarea className="w-full border border-border rounded-sm p-2 text-xs"
          rows={2} placeholder="Catatan persetujuan" value={note}
          onChange={(e) => setNote(e.target.value)} data-testid="expenses-plan-note" />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy || plan.status === "APPROVED"}
            data-testid="expenses-plan-save"
            onClick={() => send("", { action: "DRAFT" }, "Draf rencana disimpan.")}>
            Simpan Draf
          </Button>
          <Button size="sm" variant="outline" disabled={busy || plan.status === "APPROVED"}
            data-testid="expenses-plan-propose"
            onClick={() => send("", { action: "PROPOSED" }, "Rencana diajukan untuk persetujuan.")}>
            Ajukan Persetujuan
          </Button>
          <Button size="sm" disabled={busy || plan.status !== "PROPOSED"}
            data-testid="expenses-plan-approve"
            onClick={() => send("/approve", { approval_note: note },
              "Rencana sampling disetujui.")}>
            Setujui Rencana Sampling
          </Button>
          <Button size="sm" variant="outline" disabled={busy || !plan.plan_id}
            data-testid="expenses-plan-supersede"
            onClick={() => send("/supersede", { reason: note },
              "Rencana digantikan.")}>
            Supersede
          </Button>
        </div>
        <div className="text-muted-foreground">{plan.basis}</div>
      </div>
    </div>
  );
}

function BankTrace({ result }) {
  if (!result) return <Empty id="expenses-bank-empty" />;
  const controls = result.controls || {};
  if (result.summary?.status === "BANK_SOURCE_NOT_REGISTERED") {
    return (
      <div className="space-y-2 text-xs" data-testid="expenses-bank-unregistered">
        <div>Status<Badge status="BANK_SOURCE_NOT_REGISTERED" /></div>
        <div className="text-amber-800">{result.basis}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-bank">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Figure label="Rekening terdaftar" value={controls.bank_accounts_registered} />
        <Figure label="Beban dibayar langsung" value={controls.expenses_with_a_direct_bank_line} />
        <Figure label="Tertelusur ke bank" value={controls.direct_bank_expenses_traced} />
        <Figure label="Tidak ditemukan" value={controls.direct_bank_expenses_not_found} />
        <Figure label="Melalui hutang usaha" value={controls.expenses_settled_through_payable} />
        <Figure label="Pembayaran sebagian" value={controls.partial_payments} />
        <Figure label="Cakupan" value={controls.coverage_of_direct_bank_expenses} />
        <Figure label="Pembayaran tanpa beban"
          value={result.summary?.payments_without_expense} />
      </div>
      <div className="text-muted-foreground" data-testid="expenses-bank-coverage-note">
        {controls.note}
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>No. sumber</th><th>Tanggal</th><th>Pemasok</th>
          <th className="text-right">Nilai beban</th><th>Tanggal bank</th>
          <th className="text-right">Nilai bank</th><th>Status</th><th>Bukti</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.source_number}</td><td>{row.posting_date}</td><td>{row.vendor || "—"}</td>
            <td className="text-right tabular-nums">{rupiah(row.net_expense)}</td>
            <td>{row.bank_date || "—"}</td>
            <td className="text-right tabular-nums">
              {row.bank_amount ? rupiah(row.bank_amount) : "—"}</td>
            <td>{text(row.status)}</td><td>{(row.evidence || []).join(", ")}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Samples({ samples = [], coverage, plan }) {
  const selected = samples.filter((s) => s.status === "SELECTED");
  if (!selected.length) return <Empty id="expenses-samples-empty" />;
  const official = plan?.status === "APPROVED";
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-samples">
      <div className="font-medium" data-testid="expenses-sample-status">
        {official ? "Sampel Disetujui" : "Sampel Usulan"}
        <Badge status={official ? "APPROVED_SAMPLE" : "SYSTEM_GENERATED_PROPOSED_SAMPLE"} />
        {!official && (
          <span className="ml-2 font-normal text-amber-800">
            Pemilihan ini dihasilkan sistem dan belum disetujui auditor; belum menjadi sampel audit.
          </span>)}
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kerangka</th><th>Metode</th><th>Seed</th><th className="text-right">Populasi</th>
          <th className="text-right">Ukuran</th><th className="text-right">Nilai sampel</th>
          <th>Dasar pemilihan</th></tr></thead>
        <tbody>{selected.map((s, index) => (
          <tr key={index}>
            <td>{UNIVERSE_LABELS[s.universe] || s.universe}</td>
            <td>{s.sampling_method}</td><td className="font-mono">{s.seed ?? "—"}</td>
            <td className="text-right tabular-nums">{s.population_size}</td>
            <td className="text-right tabular-nums">{s.sample_size}</td>
            <td className="text-right tabular-nums">{rupiah(s.sample_value)}</td>
            <td className="max-w-md">{s.selection_basis}</td>
          </tr>))}
        </tbody>
      </table>
      {coverage && (
        <div data-testid="expenses-coverage">
          <div>
            {coverage.distinct_transactions_selected} transaksi berbeda terpilih ·{" "}
            {coverage.count_coverage} · cakupan nilai {coverage.value_coverage} · belum diuji{" "}
            {rupiah(coverage.untested_value)}
          </div>
          <div className="text-amber-800 mt-1">{coverage.note}</div>
        </div>
      )}
    </div>
  );
}

function Substantive({ samples = [] }) {
  const rows = samples.filter((s) => s.status === "SELECTED")
    .flatMap((s) => s.selected || []);
  if (!rows.length) return <Empty id="expenses-substantive-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-substantive">
      <div>{rows.length} baris pengujian disiapkan. Seluruh kolom pengujian dibiarkan kosong untuk
        diisi auditor; sistem tidak menjawab satu pun.</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>ID populasi</th><th>Tanggal</th><th>Pemasok</th><th>Uraian</th>
          <th>No. sumber</th><th className="text-right">Nilai</th><th>Kategori</th>
          <th>Metode</th><th>Dokumen pendukung</th><th>Nilai sesuai</th>
          <th>Simpulan auditor</th></tr></thead>
        <tbody>{rows.slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td className="max-w-xs">{row.expense_population_id}</td>
            <td>{row.posting_date}</td><td>{row.vendor || "—"}</td>
            <td className="max-w-md">{row.description}</td>
            <td>{row.source_number}</td>
            <td className="text-right tabular-nums">{rupiah(row.net_expense)}</td>
            <td>{row.expense_category}</td><td>{row.selection_method}</td>
            <td className="text-muted-foreground">{row.supporting_document_obtained ?? "—"}</td>
            <td className="text-muted-foreground">{row.amount_agrees ?? "—"}</td>
            <td className="text-muted-foreground">{row.auditor_conclusion ?? "Belum disimpulkan"}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Cutoff({ result }) {
  if (!result) return <Empty id="expenses-cutoff-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-cutoff">
      <div>
        {Object.entries(result.windows || {}).map(([window, entry]) => (
          <span key={window} className="mr-3">±{window} hari: {entry.count}</span>))}
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>No. sumber</th><th>Tanggal pembukuan</th><th>Tanggal dokumen</th>
          <th>Kategori</th><th className="text-right">Nilai</th><th>Hari</th><th>Klasifikasi</th>
          <th>Simpulan auditor</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.source_number}</td><td>{row.posting_date}</td><td>{row.document_date}</td>
            <td>{row.expense_category}</td>
            <td className="text-right tabular-nums">{rupiah(row.net_expense)}</td>
            <td className="text-right tabular-nums">{row.days_from_period_end}</td>
            <td>{text(row.classification)}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.basis}</div>
    </div>
  );
}

function Capital({ result }) {
  if (!result) return <Empty id="expenses-capital-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-capital">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Status</th><th className="text-right">Transaksi</th></tr></thead>
        <tbody>{Object.entries(result.findings || {}).map(([name, count]) => (
          <tr key={name}><td>{text(name)}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>No. sumber</th><th>Tanggal</th><th>Uraian</th>
          <th className="text-right">Nilai</th><th>Status</th><th>Alasan</th>
          <th>Simpulan auditor</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.source_number}</td><td>{row.posting_date}</td>
            <td className="max-w-md">{row.description}</td>
            <td className="text-right tabular-nums">{rupiah(row.net_expense)}</td>
            <td>{text(row.status)}</td><td className="max-w-md">{row.reason}</td>
            <td className="text-muted-foreground">Belum disimpulkan</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-amber-800" data-testid="expenses-capital-basis">{result.basis}</div>
    </div>
  );
}

function Open({ view }) {
  const rows = [];
  const clean = {
    expense_vs_purchase: ["MATCHED_EXPENSE_PURCHASE", "PURCHASE_NOT_EXPECTED"],
    expense_vs_vat: ["MATCHED_EXPENSE_VAT", "VAT_NOT_APPLICABLE"],
    expense_vs_wht: ["MATCHED_EXPENSE_WHT", "WHT_NOT_APPLICABLE"],
    expense_vs_payment: ["MATCHED_PAYMENT", "PAID_DIRECTLY_FROM_BANK", "SETTLED_THROUGH_PAYABLE"],
  };
  Object.entries(clean).forEach(([key, ok]) => {
    (view[key]?.items || []).forEach((item) => {
      if (!ok.includes(item.status))
        rows.push({ source: key, key: item.source_number, status: item.status,
          amount: item.net_expense });
    });
  });
  return (
    <table className="w-full audit-table text-xs" data-testid="expenses-open">
      <thead><tr><th>Rekonsiliasi</th><th>No. sumber</th><th>Status</th>
        <th className="text-right">Nilai</th><th>Simpulan auditor</th></tr></thead>
      <tbody>{rows.slice(0, 300).map((row, index) => (
        <tr key={index}>
          <td>{row.source}</td><td>{row.key}</td><td>{text(row.status)}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td className="text-muted-foreground">Belum disimpulkan</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Evidence({ rows = [], population }) {
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-evidence">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Sumber</th><th>Populasi logis</th><th>Dataset</th>
          <th className="text-right">Baris</th></tr></thead>
        <tbody>{rows.map((row, index) => (
          <tr key={index}>
            <td>{row.source}</td><td className="font-mono">{row.logical_id}</td>
            <td className="font-mono max-w-md">{(row.datasets || []).join(", ")}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
          </tr>))}
        </tbody>
      </table>
      {!!(population?.datasets_outside_the_logical_population || []).length && (
        <div className="text-amber-800" data-testid="expenses-outside-note">
          {population.datasets_outside_the_logical_population.length} dataset buku besar berada di
          luar populasi logis yang disetujui dan tidak digunakan.
        </div>
      )}
    </div>
  );
}

function Readiness({ readiness }) {
  if (!readiness) return <Empty id="expenses-readiness-empty" />;
  return (
    <div className="space-y-2 text-xs" data-testid="expenses-readiness">
      <div className="font-medium">
        <span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[readiness.status] || ""}`}
          data-testid="expenses-readiness-status">{text(readiness.status)}</span>
      </div>
      <table className="w-full audit-table text-xs" data-testid="expenses-gates">
        <thead><tr><th>Gerbang</th><th>Status</th><th>Dasar</th></tr></thead>
        <tbody>{Object.entries(readiness.gates || {}).map(([name, entry]) => (
          <tr key={name}>
            <td>{GATE_LABELS[name] || name}</td>
            <td><span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[entry.status] || ""}`}>
              {text(entry.status)}</span></td>
            <td className="max-w-xl">{entry.basis}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{readiness.basis}</div>
    </div>
  );
}
